import type {
  AttentionBucket,
  AttentionItem,
  AttentionPriority,
} from '@platform/schema/attention';

/**
 * What to do about one item, and where doing it happens — ADMIN-15.
 *
 * Here rather than in the API, which returns `{ kind, id }` and never a URL. The console
 * owns its own routes: an API that names them cannot be re-skinned, and a route renamed
 * in `router.tsx` would leave a dead link the server was confident about.
 *
 * One action per item, not three. The brief asks that every item have an obvious next
 * action, and `Review Issue · Apply Fix · Ignore` is three of them — two of which already
 * exist inside the review workspace, one click past this label. A queue whose job is to
 * clear itself is worse at it with three buttons per row.
 */
export interface NextAction {
  readonly label: string;
  readonly to: string;
}

const LESSON_ACTION: Readonly<Record<AttentionBucket, string>> = {
  ISSUES: 'Review issue',
  DRAFTS: 'Finish draft',
  WARNINGS: 'Review warning',
  NEEDS_REVIEW: 'Review lesson',
  READY_TO_PUBLISH: 'Preview and publish',
  // A lesson is never `PROCESSING` — only a run is. Named anyway, because an unnamed
  // key here is a crash on a row rather than a compile error on the day it becomes
  // reachable.
  PROCESSING: 'Open lesson',
};

/**
 * Partial, unlike `LESSON_ACTION`, because `jobAttention` can only ever return these two.
 * Spelling out four unreachable rows that all say `Open run` claims a coverage the
 * classifier does not have.
 */
const JOB_ACTION: Readonly<Partial<Record<AttentionBucket, string>>> = {
  // Not "the failed run": this bucket holds PARTIAL as well, and a run where four of
  // five rows worked is not a failed one. The row's reason line says which it is.
  ISSUES: 'Open the run',
  PROCESSING: 'Watch the run',
};

export function nextAction(item: AttentionItem): NextAction {
  return item.target.kind === 'LESSON'
    ? {
        label: LESSON_ACTION[item.bucket],
        to: `/authoring/lessons/${item.target.id}`,
      }
    : {
        label: JOB_ACTION[item.bucket] ?? 'Open run',
        to: `/authoring/jobs/${item.target.id}`,
      };
}

/** What each bucket is called on screen, so the cards and the chips cannot disagree. */
export const BUCKET_LABEL: Readonly<Record<AttentionBucket, string>> = {
  ISSUES: 'Issues',
  DRAFTS: 'Drafts',
  WARNINGS: 'Warnings',
  NEEDS_REVIEW: 'Need review',
  READY_TO_PUBLISH: 'Ready to publish',
  PROCESSING: 'Processing',
};

/**
 * How loudly a row is drawn.
 *
 * `NORMAL` is `default` and not a colour. Three coloured rows in a list of thirty are a
 * priority; thirty coloured rows are wallpaper, and the one thing this screen must keep
 * is the ability to see the critical row without reading.
 */
export const PRIORITY_COLOUR: Readonly<
  Record<AttentionPriority, 'error' | 'warning' | 'default'>
> = {
  CRITICAL: 'error',
  NEEDS_ATTENTION: 'warning',
  NORMAL: 'default',
};

export const PRIORITY_LABEL: Readonly<Record<AttentionPriority, string>> = {
  CRITICAL: 'Critical',
  NEEDS_ATTENTION: 'Needs attention',
  NORMAL: 'Normal',
};
